<template>
  <BaseDropdownWrapper
    ref="dropdownWrapperRef"
    class="base-time-picker"
    :class="{ 'base-time-picker--with-full': props.withFull }"
    :with-full="props.withFull"
    placement="bottom-start"
    strategy="fixed"
    :off="props.disabled"
    width="fill"
    :with-rounded="true"
    max-height="none"
    :with-overflow="false"
  >
    <template #control>
      <BaseInput
        ref="inputRef"
        type="text"
        :size="props.size"
        :placeholder="props.placeholder"
        inputmode="numeric"
        :readonly="props.readonly"
        :disabled="props.disabled"
        :with-full="props.withFull"
        :tabindex="props.tabindex"
        :mask="MASK_OF_TIME"
        :model-value="inputText"
        :variant="props.variant"
        :with-clear="props.withClear"
        :append-icon="props.appendIcon"
        :append-icon-color="props.appendIconColor"
        :error-message="props.errorMessage"
        data-testid="timepicker-input"
        @update:model-value="handleInputUpdate"
        @cleared="handleClear"
        @click-append-icon="handleAppendIconClick"
      />
    </template>
    <template #dropdown="{ handleClose }">
      <div
        class="base-time-picker__panel"
        data-testid="timepicker-panel"
      >
        <div class="base-time-picker__columns">
          <div class="base-time-picker__col">
            <div
              ref="hoursScrollRef"
              class="base-time-picker__col-scroll"
              data-testid="timepicker-hours-col"
              @scroll.passive="handleHoursScroll"
            >
              <div class="base-time-picker__col-pad">
                <button
                  v-for="h in HOURS"
                  :key="h"
                  type="button"
                  class="base-time-picker__cell"
                  :class="{
                    'base-time-picker__cell--active': draftHour === h,
                    'base-time-picker__cell--disabled': isHourCellDisabled(h),
                  }"
                  :disabled="isHourCellDisabled(h)"
                  :data-testid="`timepicker-hour-${h}`"
                  @click="handleHourClick(h)"
                >
                  {{ pad2(h) }}
                </button>
              </div>
            </div>
          </div>
          <span
            class="base-time-picker__sep"
            aria-hidden="true"
          >:</span>
          <div class="base-time-picker__col">
            <div
              ref="minutesScrollRef"
              class="base-time-picker__col-scroll"
              data-testid="timepicker-minutes-col"
              @scroll.passive="handleMinutesScroll"
            >
              <div class="base-time-picker__col-pad">
                <button
                  v-for="m in MINUTES"
                  :key="m"
                  type="button"
                  class="base-time-picker__cell"
                  :class="{
                    'base-time-picker__cell--active': draftMinute === m,
                    'base-time-picker__cell--disabled': isMinuteCellDisabled(m),
                  }"
                  :disabled="isMinuteCellDisabled(m)"
                  :data-testid="`timepicker-minute-${m}`"
                  @click="handleMinuteClick(m)"
                >
                  {{ pad2(m) }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="base-time-picker__footer">
          <BaseButton
            size="mini"
            with-full
            data-testid="cancel-time-picker-btn"
            @click="handleCancelPanel(handleClose)"
          >
            Назад
          </BaseButton>
          <BaseButton
            size="mini"
            with-full
            variant="primary"
            :disabled="isDraftCellDisabled"
            data-testid="apply-time-picker-btn"
            @click="handleApplyPanel(handleClose)"
          >
            Применить
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseDropdownWrapper>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, unref, watch } from 'vue'
import { MASK_OF_TIME } from '../../const/masks'
import BaseButton from '../button/index.vue'
import BaseDropdownWrapper from '../dropdown-wrapper/index.vue'
import BaseInput from '../input/index.vue'

interface Props {
  modelValue?: string | null
  name?: string
  tabindex?: number
  size?: 'md' | 'lg'
  variant?: 'light' | 'secondary'
  withFull?: boolean
  placeholder?: string
  appendIcon?: string
  appendIconColor?: string
  withClear?: boolean
  disabled?: boolean
  readonly?: boolean
  errorMessage?: string
  isCellDisabled?: (hour: number, minute: number) => boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  variant: 'secondary',
  appendIcon: 'vuesax:clock-linear',
  appendIconColor: 'var(--color-foreground-primary)',
  placeholder: '00:00',
  withFull: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', val: string | null): void
  (e: 'cleared', name?: string): void
  (e: 'clickAppendIcon', name?: string): void
}>()

const ITEM_HEIGHT_PX = 32

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = Array.from({ length: 60 }, (_, i) => i)

const dropdownWrapperRef = ref<InstanceType<typeof BaseDropdownWrapper> | null>(
  null,
)
const inputRef = ref<InstanceType<typeof BaseInput> | null>(null)
const hoursScrollRef = ref<HTMLElement | null>(null)
const minutesScrollRef = ref<HTMLElement | null>(null)

const inputText = ref('')
const draftHour = ref(0)
const draftMinute = ref(0)

let hoursScrollRaf = 0
let minutesScrollRaf = 0

const isDraftCellDisabled = computed(() => {
  if (!props.isCellDisabled) {
    return false
  }

  return props.isCellDisabled(draftHour.value, draftMinute.value)
})

function isHourCellDisabled(hour: number): boolean {
  if (!props.isCellDisabled) {
    return false
  }

  return MINUTES.every(minute => props.isCellDisabled!(hour, minute))
}

function isMinuteCellDisabled(minute: number): boolean {
  if (!props.isCellDisabled) {
    return false
  }

  return props.isCellDisabled(draftHour.value, minute)
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

function parseTimeToDate(value: string): Date | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) {
    return null
  }
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) {
    return null
  }
  const d = new Date()
  d.setHours(h, min, 0, 0)
  return d
}

function normalizeCompleteTimeString(s: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim())
  if (!m) {
    return null
  }
  let h = Number(m[1])
  let min = Number(m[2])
  if (h <= 23 && min <= 59) {
    return null
  }
  h = Math.min(23, Math.max(0, h))
  min = Math.min(59, Math.max(0, min))
  return `${pad2(h)}:${pad2(min)}`
}

function updateDraftFromInput(source: string, syncScroll: boolean) {
  const trimmed = source.trim()
  const digitsOnly = trimmed.replace(/\D/g, '')

  if (!trimmed || digitsOnly.length === 0) {
    draftHour.value = 0
    draftMinute.value = 0
    if (syncScroll) {
      void nextTick(() => syncScrollPositionsFromDraft())
    }
    return
  }

  const full = parseTimeToDate(trimmed)
  if (full) {
    draftHour.value = full.getHours()
    draftMinute.value = full.getMinutes()
    if (syncScroll) {
      void nextTick(() => syncScrollPositionsFromDraft())
    }
    return
  }

  const [rawH = '', rawM = ''] = trimmed.split(':')
  const hDigits = rawH.replace(/\D/g, '').slice(0, 2)
  const mDigits = rawM.replace(/\D/g, '').slice(0, 2)

  let h = draftHour.value
  let m = draftMinute.value
  let updated = false

  if (hDigits.length >= 1) {
    const hn = hDigits.length === 1
      ? Number.parseInt(hDigits, 10)
      : Number.parseInt(hDigits.slice(0, 2), 10)
    if (!Number.isNaN(hn)) {
      h = Math.min(23, Math.max(0, hn))
      updated = true
    }
  }

  if (mDigits.length >= 1) {
    if (mDigits.length === 1) {
      const mn = Number.parseInt(mDigits[0], 10)
      if (!Number.isNaN(mn)) {
        m = Math.min(59, mn * 10)
        updated = true
      }
    }
    else {
      const mn = Number.parseInt(mDigits.slice(0, 2), 10)
      if (!Number.isNaN(mn)) {
        m = Math.min(59, Math.max(0, mn))
        updated = true
      }
    }
  }

  if (updated) {
    draftHour.value = h
    draftMinute.value = m
  }

  if (syncScroll && updated) {
    void nextTick(() => syncScrollPositionsFromDraft())
  }
}

function scrollColumnTo(
  el: HTMLElement | null,
  index: number,
  maxIndex: number,
) {
  if (!el) {
    return
  }
  const i = Math.min(maxIndex, Math.max(0, index))
  el.scrollTop = i * ITEM_HEIGHT_PX
}

function syncScrollPositionsFromDraft() {
  scrollColumnTo(hoursScrollRef.value, draftHour.value, 23)
  scrollColumnTo(minutesScrollRef.value, draftMinute.value, 59)
}

function handleHoursScroll() {
  const el = hoursScrollRef.value
  if (!el) {
    return
  }
  cancelAnimationFrame(hoursScrollRaf)
  hoursScrollRaf = requestAnimationFrame(() => {
    const h = Math.min(23, Math.max(0, Math.round(el.scrollTop / ITEM_HEIGHT_PX)))
    draftHour.value = h
  })
}

function handleMinutesScroll() {
  const el = minutesScrollRef.value
  if (!el) {
    return
  }
  cancelAnimationFrame(minutesScrollRaf)
  minutesScrollRaf = requestAnimationFrame(() => {
    const m = Math.min(59, Math.max(0, Math.round(el.scrollTop / ITEM_HEIGHT_PX)))
    draftMinute.value = m
  })
}

function snapHoursScroll() {
  const el = hoursScrollRef.value
  if (!el) {
    return
  }
  const h = Math.min(23, Math.max(0, Math.round(el.scrollTop / ITEM_HEIGHT_PX)))
  el.scrollTo({ top: h * ITEM_HEIGHT_PX, behavior: 'smooth' })
  draftHour.value = h
}

function snapMinutesScroll() {
  const el = minutesScrollRef.value
  if (!el) {
    return
  }
  const m = Math.min(59, Math.max(0, Math.round(el.scrollTop / ITEM_HEIGHT_PX)))
  el.scrollTo({ top: m * ITEM_HEIGHT_PX, behavior: 'smooth' })
  draftMinute.value = m
}

function refocusInput() {
  inputRef.value?.focus()
}

function handleHourClick(h: number) {
  if (isHourCellDisabled(h)) {
    return
  }

  draftHour.value = h
  scrollColumnTo(hoursScrollRef.value, h, 23)
  void nextTick(() => refocusInput())
}

function handleMinuteClick(m: number) {
  if (isMinuteCellDisabled(m)) {
    return
  }

  draftMinute.value = m
  scrollColumnTo(minutesScrollRef.value, m, 59)
  void nextTick(() => refocusInput())
}

function handleAppendIconClick() {
  emit('clickAppendIcon', props.name)
}

function handleApplyPanel(close: () => void) {
  if (isDraftCellDisabled.value) {
    return
  }

  const next = `${pad2(draftHour.value)}:${pad2(draftMinute.value)}`
  inputText.value = next
  emit('update:modelValue', next)
  close()
  void nextTick(() => refocusInput())
}

function handleCancelPanel(close: () => void) {
  close()
  void nextTick(() => refocusInput())
}

function handleInputUpdate(value: string | number | null) {
  let s = value == null ? '' : String(value)
  const clamped = normalizeCompleteTimeString(s)
  if (clamped !== null) {
    s = clamped
  }
  inputText.value = s

  const panelOpen = Boolean(unref(dropdownWrapperRef.value?.isOpen))
  updateDraftFromInput(s, panelOpen)

  const parsed = parseTimeToDate(s)
  if (parsed && s.trim().length === 5) {
    emit('update:modelValue', s.trim())
    return
  }
  if (s.trim() === '') {
    emit('update:modelValue', '')
  }
}

function handleClear() {
  inputText.value = ''
  emit('update:modelValue', '')
  emit('cleared', props.name)
  dropdownWrapperRef.value?.handleClose()
  void nextTick(() => refocusInput())
}

watch(
  () => props.modelValue,
  (value) => {
    if (value == null || value === '') {
      inputText.value = ''
      draftHour.value = 0
      draftMinute.value = 0
      return
    }
    let next = String(value)
    const clamped = normalizeCompleteTimeString(next)
    if (clamped !== null) {
      next = clamped
      emit('update:modelValue', clamped)
    }
    inputText.value = next
    updateDraftFromInput(inputText.value, false)
  },
  { immediate: true },
)

watch(
  () => unref(dropdownWrapperRef.value?.isOpen),
  async (open) => {
    if (!open) {
      return
    }
    updateDraftFromInput(
      inputText.value || String(props.modelValue ?? ''),
      false,
    )
    await nextTick()
    syncScrollPositionsFromDraft()
    refocusInput()
  },
)

function attachScrollEnd(el: HTMLElement | null, snap: () => void) {
  if (!el) {
    return
  }
  el.addEventListener('scrollend', snap)
}

function detachScrollEnd(el: HTMLElement | null, snap: () => void) {
  if (!el) {
    return
  }
  el.removeEventListener('scrollend', snap)
}

watch(hoursScrollRef, (el, prev) => {
  detachScrollEnd(prev, snapHoursScroll)
  attachScrollEnd(el, snapHoursScroll)
})

watch(minutesScrollRef, (el, prev) => {
  detachScrollEnd(prev, snapMinutesScroll)
  attachScrollEnd(el, snapMinutesScroll)
})
</script>

<style lang="scss" scoped>
.base-time-picker {
  display: inline-block;
  width: auto;
  max-width: 100%;

  &:deep(.base-dropdown-wrapper__dropdown) {
    overflow: hidden;
    border: 1px solid var(--color-surface-border);
    border-radius: var(--radius-sm);
  }

  &--with-full {
    display: block;
    width: 100%;
  }

  &__panel {
    box-sizing: border-box;
    min-width: rem(220px);
  }

  &__columns {
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: rem(4px);
    margin-bottom: rem(8px);
  }

  &__col {
    flex: 1;
    min-width: 0;
  }

  &__sep {
    flex: 0 0 auto;
    align-self: center;
    font-size: rem(16px);
    font-weight: 700;
    color: var(--color-foreground-main, #333);
    padding: 0 rem(2px);
    user-select: none;
  }

  &__col-scroll {
    position: relative;
    height: rem(224px);
    overflow: hidden auto;
    scrollbar-width: thin;
    border-radius: rem(8px);
    background: rgb(51 51 51 / 4%);

    &::before {
      pointer-events: none;
      content: '';
      position: absolute;
      z-index: 0;
      left: 0;
      right: 0;
      top: 50%;
      height: rem(32px);
      transform: translateY(-50%);
      border-block: 1px solid rgb(51 51 51 / 12%);
      background: rgb(9 99 242 / 8%);
    }
  }

  &__col-pad {
    position: relative;
    z-index: 1;
    padding-top: rem(96px);
    padding-bottom: rem(96px);
  }

  &__cell {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: rem(32px);
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: rem(14px);
    line-height: 1;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--color-foreground-main, #333);
    cursor: pointer;
    transition:
      background 0.12s ease,
      color 0.12s ease;

    &:hover {
      background: rgb(51 51 51 / 6%);
    }

    &--active {
      color: var(--color-surface-primary, #0963f2);
    }

    &--disabled,
    &:disabled {
      color: var(--color-foreground-caption);
      opacity: 0.45;
      cursor: not-allowed;
      pointer-events: none;

      &:hover {
        background: transparent;
      }
    }
  }

  &__footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: rem(8px);
  }
}
</style>
